// Campaigns Table Component
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Filter, Eye, SlidersHorizontal, X } from "lucide-react";
import CampaignFilters from "./CampaignFilters";
import CampaignActions from "./CampaignActions";
import TablePagination from "./TablePagination";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const statusConfig = {
  LAUNCHED: { label: "LAUNCHED", variant: "default", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  "OUT FOR APPROVAL": { label: "OUT FOR APPROVAL", variant: "default", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  COMPLETED: { label: "COMPLETED", variant: "secondary", color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200" },
  CREATED: { label: "CREATED", variant: "outline", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  "IN PROGRESS": { label: "IN PROGRESS", variant: "outline", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  PAUSED: { label: "PAUSED", variant: "destructive", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
};

const CampaignsTable = ({
  campaigns,
  pagination,
  isLoading,
  filters,
  filterOptions,
  onFilterChange,
  onPageChange,
  onApprove,
  onReject,
  onStatusChange,
  onEditCampaignName,
  onDelete,
  currentPage,
})=> {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState(filters.search || "");

  const hasActiveFilters = filters.brand || filters.region || filters.status || filters.sortBy || filters.search;

  const clearAllFilters = () => {
    onFilterChange("brand", "");
    onFilterChange("region", "");
    onFilterChange("status", "");
    onFilterChange("sortBy", "");
    onFilterChange("search", "");
    setSearchQuery("");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    onFilterChange("search", searchQuery);
  };

  const handleSearchInput = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value === "") {
      onFilterChange("search", "");
    }
  };

  const getStatusDisplay = (status) => {
    if (!status) {
      return statusConfig.CREATED; // Default to CREATED for null/undefined status
    }
    return statusConfig[status] || statusConfig.CREATED;
  };

  const getBrandInitial = (brand) => {
    return brand ? brand.charAt(0).toUpperCase() : "?";
  };

  const getBrandColor = (brandName) => {
    const colors = [
      "bg-blue-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-green-500",
      "bg-orange-500",
      "bg-red-500",
    ];
    const hash = brandName?.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0;
    return colors[hash % colors.length];
  };

  return (
    <Card className="overflow-hidden border-border/40 shadow-lg">
      <CardHeader className="border-b bg-muted/50 space-y-4 pb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              Campaigns Management
              {campaigns && campaigns.length > 0 && (
                <Badge variant="secondary" className="ml-2 font-normal">
                  {campaigns.length} {campaigns.length === 1 ? "campaign" : "campaigns"}
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1.5">
              Monitor and manage all advertising campaigns
            </CardDescription>
          </div>
          
          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="relative flex-1 md:flex-initial md:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={handleSearchInput}
                className="pl-10 pr-10 h-10 bg-background/60 backdrop-blur-sm border-border/60 focus:border-primary/60 transition-all"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
                  onClick={() => {
                    setSearchQuery("");
                    onFilterChange("search", "");
                  }}
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </form>
            
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "h-10 w-10 transition-all",
                hasActiveFilters && !showFilters && "border-primary bg-primary/5 text-primary"
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="hidden md:flex h-10 gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="pt-4 border-t animate-in slide-in-from-top-2 duration-200">
            <CampaignFilters
              filters={filters}
              filterOptions={filterOptions}
              onFilterChange={onFilterChange}
            />
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-0">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 animate-pulse bg-muted/30 border-b border-border/40 last:border-b-0" />
            ))}
          </div>
        ) : !campaigns || campaigns.length === 0 ? (
          <div className="flex flex-col h-96 items-center justify-center text-muted-foreground p-8">
            <div className="text-center space-y-3">
              <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Search className="h-8 w-8 text-muted-foreground/50" />
              </div>
              <p className="text-lg font-semibold text-foreground">No campaigns found</p>
              <p className="text-sm max-w-md">
                {hasActiveFilters 
                  ? "Try adjusting your filters or search query to find what you're looking for"
                  : "No campaigns have been created yet"}
              </p>
              {hasActiveFilters && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearAllFilters}
                  className="mt-4"
                >
                  Clear All Filters
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30 border-b-2">
                    <TableHead className="w-16"></TableHead>
                    <TableHead className="font-semibold">CAMPAIGN</TableHead>
                    <TableHead className="font-semibold">REPRESENTATIVE</TableHead>
                    <TableHead className="font-semibold">DATE SUBMITTED</TableHead>
                    <TableHead className="font-semibold">STATUS</TableHead>
                    <TableHead className="text-right font-semibold">BUDGET</TableHead>
                    <TableHead className="text-center font-semibold">ACTION</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(campaigns || []).map((campaign, index) => {
                    const statusInfo = getStatusDisplay(campaign.status);
                    return (
                      <TableRow 
                        key={campaign.campaign_id}
                        className="group hover:bg-muted/50 transition-colors border-b border-border/40"
                      >
                        {/* Brand Avatar */}
                        <TableCell className="py-4">
                          <Avatar className={cn("h-11 w-11", getBrandColor(campaign.brand || 'Default'))}>
                            <AvatarFallback className="bg-transparent text-white font-bold text-sm">
                              {getBrandInitial(campaign.brand || 'N/A')}
                            </AvatarFallback>
                          </Avatar>
                        </TableCell>

                        {/* Campaign Info */}
                        <TableCell className="py-4">
                          <div className="space-y-1">
                            <button
                              onClick={() => navigate(`/business-admin/campaign/${campaign.campaign_id}`)}
                              className="font-semibold text-foreground hover:text-primary transition-colors text-left group-hover:underline underline-offset-2 decoration-2"
                            >
                              {campaign.campaign_name || 'Untitled Campaign'}
                            </button>
                            <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                              <Badge variant="outline" className="font-normal text-xs px-2 py-0">
                                {campaign.campaign_type || 'N/A'}
                              </Badge>
                            </div>
                          </div>
                        </TableCell>

                        {/* Representative */}
                        <TableCell className="py-4">
                          <div className="space-y-0.5">
                            <div className="font-medium text-foreground">
                              {campaign.representative?.name || "N/A"}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {campaign.representative?.email || ""}
                            </div>
                          </div>
                        </TableCell>

                        {/* Date Submitted */}
                        <TableCell className="py-4">
                          <span className="text-sm text-foreground font-medium">
                            {campaign.date_submitted 
                              ? new Date(campaign.date_submitted).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })
                              : 'N/A'
                            }
                          </span>
                        </TableCell>

                        {/* Status Badge */}
                        <TableCell className="py-4">
                          <Badge className={cn(statusInfo.color, "font-medium shadow-sm")}>
                            {statusInfo.label || 'UNKNOWN'}
                          </Badge>
                        </TableCell>

                        {/* Budget */}
                        <TableCell className="text-right font-semibold text-foreground py-4">
                          {campaign.formatted_budget || '$0'}
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-center py-4">
                          <CampaignActions
                            campaign={campaign}
                            onApprove={onApprove}
                            onReject={onReject}
                            onStatusChange={onStatusChange}
                            onEditCampaignName={onEditCampaignName}
                            onDelete={onDelete}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="border-t bg-muted/30 px-6 py-4">
              <TablePagination
                pagination={pagination}
                currentPage={currentPage}
                onPageChange={onPageChange}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(CampaignsTable);
